import { Artikel, LagerplatzInhalt } from "@softapps/lager/core";
import {
  ActionStore,
  createAction,
  createGetter,
  createMutation,
  KeyStore,
  mutate,
  on,
  select,
  SOFTLINE_SERVICE_UUID,
  StoreFeature,
  SubscriptionStore
} from "@softline/core";
import { lastValueFrom } from "rxjs";
import { LagerstandService } from "../services/lagerstand.service";

export interface State extends ActionStore.State, SubscriptionStore.State, KeyStore.State  {
  artikel?: Artikel;
  lagerstand?: LagerplatzInhalt[];
}

export class LagerstandStore {
  static mutations = {
    setArtikel: createMutation<State, Artikel>('setArtikel'),
    setLagerstand: createMutation<State, LagerplatzInhalt[]>('setLagerstand'),
    setPalette: createMutation<State, LagerplatzInhalt>('lagerstand setPalette'),
    reset: createMutation<State>('reset'),
  };
  static getters = {
    lagerstand: createGetter<State, LagerplatzInhalt[]>('lagerstand'),
    artikel: createGetter<State, Artikel>('artikel')
  };
  static actions = {
    loadPalette: createAction<State, string, LagerplatzInhalt>('lagerstand loadPalette'),
    loadLagerstand: createAction<State, string | number, LagerplatzInhalt[]>('loadLagerstand'),
  };

  static feature: StoreFeature<State> = {
    initialState: {
      ...ActionStore.feature.initialState,
      ...KeyStore.feature.initialState,
      ...SubscriptionStore.feature.initialState
    },
    mutations: [
      ...ActionStore.feature.mutations,
      ...KeyStore.feature.mutations,
      ...SubscriptionStore.feature.mutations,
      mutate(LagerstandStore.mutations.setLagerstand,
        ({state, params}) => ({...state, lagerstand: params})
      ),
      mutate(LagerstandStore.mutations.setArtikel,
        ({state, params}) => ({...state, artikel: params})
      ),
      mutate(LagerstandStore.mutations.reset,
        ({state, params}) => ({...state, lagerplatz: undefined, artikel: undefined, lagersymbol: undefined})
      ),
      mutate(LagerstandStore.mutations.setPalette,
        ({state, params}) => ({...state, palette: params})
      ),
    ],
    getters: [
      ...ActionStore.feature.getters,
      ...KeyStore.feature.getters,
      ...SubscriptionStore.feature.getters,
      select(LagerstandStore.getters.lagerstand, ({state}) => state.lagerstand),
      select(LagerstandStore.getters.artikel, ({state}) => state.artikel)
    ],
    actions: [
      ...ActionStore.feature.actions,
      ...KeyStore.feature.actions,
      ...SubscriptionStore.feature.actions,
      on(LagerstandStore.actions.loadLagerstand, async ({state, commit, featureName, injector, params}) => {
        const service = injector.get(LagerstandService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();
        const subscriptionState$ = SubscriptionStore.handleSubscriptionState(
          service.getLagerstand(params),
          featureName,
          commit,
          token
        );
        const result = await lastValueFrom(
          ActionStore.handleObservableActionState(
            subscriptionState$,
            featureName,
            commit,
            LagerstandStore.actions.loadLagerstand.name,
            token
          )
        );
        commit(featureName, LagerstandStore.mutations.setLagerstand, result);
        return result;
      }),
      on(LagerstandStore.actions.loadPalette, async ({state, commit, featureName, injector, params}) => {
        const service = injector.get(LagerstandService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();
        const subscriptionState$ = SubscriptionStore.handleSubscriptionState(
          service.getPalette(params),
          featureName,
          commit,
          token
        );
        const result = await lastValueFrom(
          ActionStore.handleObservableActionState(
            subscriptionState$,
            featureName,
            commit,
            LagerstandStore.actions.loadPalette.name,
            token
          )
        );
        commit(featureName, LagerstandStore.mutations.setPalette, result);
        return result;
      }),
    ]
  }
}
